/**
 * The editor canvas (10-web-admin § 6.1, § 6.3).
 *
 * SVG with pointer events, not canvas: a plan has under a hundred objects, the
 * DOM nodes are addressable by Playwright and screen readers, and text stays
 * crisp at every zoom. The geometry helpers are the same ones the member
 * `FloorPlan` uses (`@cowoo/shared/ui-core`), so a desk sits in the same place
 * here and in the preview.
 *
 * This component decides nothing: every drag, nudge and snap goes through the
 * pure functions in `@cowoo/shared`, and the store owns the document.
 */

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { deskStyle, type DeskState } from '@cowoo/shared/ui-core';
import {
  moveObject,
  smartGuides,
  type Bounds,
  type EditorObject,
  type Guide,
  RESIZABLE_KINDS,
  RESIZE_HANDLES,
  resizeObject,
  type ResizeHandle,
} from '@cowoo/shared';

export interface CanvasProps {
  objects: EditorObject[];
  selection: string[];
  bounds: Bounds;
  gridCm: number;
  snap: boolean;
  /** § 6.3 — 0.25 to 4; the SVG is scaled and the wrapper scrolls. */
  zoom?: number;
  pxPerCm: number;
  backgroundUrl?: string | null;
  backgroundOpacity?: number;
  /**
   * The picture's size **in centimetres** — its pixel size divided by the
   * scale that was measured on it (§ 6.2).
   *
   * Given rather than assumed, because stretching the image to the plan bounds
   * is the one thing the scale exists to prevent: a wall measured at 12,40 m
   * has to stay 12,40 m long under the desks placed against it.
   */
  backgroundSizeCm?: { widthCm: number; heightCm: number } | null;
  onSelect: (ids: string[]) => void;
  onMove: (moved: EditorObject[]) => void;
  /** A drag or a resize begins and ends; the editor's history counts it once. */
  onGesture?: (phase: 'start' | 'end') => void;
  /** A press on empty canvas while a placing tool is active (§ 6.3). */
  /**
   * A click on the sheet with a placing tool. A room or an office is *drawn*
   * (§ 6.3: "drag a rectangle"), so those arrive with the size that was
   * dragged; a plain click leaves `size` out and the default applies.
   */
  onPlace?: (xCm: number, yCm: number, size?: { wCm: number; hCm: number }) => void;
  /** The active tool: the two rectangle tools draw, the others place on click. */
  tool?: 'select' | 'desk' | 'room' | 'locker' | 'parking' | 'office';
  /** Live states from `/occupancy`, for the "Belegung heute" overlay. */
  liveStateOf?: (resourceId: string) => DeskState;
}

interface Resize {
  pointerId: number;
  handle: ResizeHandle;
  fromXCm: number;
  fromYCm: number;
  original: EditorObject;
}

/** A grip is this many screen pixels square, at every zoom. */
const GRIP_PX = 10;
/** § 6.3 — the tools that draw a rectangle rather than place a fixed size. */
const DRAWN_KINDS: readonly string[] = ['room', 'office'];
/** Below this, a drag with a drawing tool was a click: the default size is placed. */
const DRAW_MIN_CM = 20;
const GRIP_CURSORS: Record<ResizeHandle, string> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
};

interface Drag {
  pointerId: number;
  /** Where the pointer went down, in cm. */
  fromXCm: number;
  fromYCm: number;
  /** The objects as they were when the drag started. */
  original: EditorObject[];
}

export function Canvas({
  objects,
  selection,
  bounds,
  gridCm,
  snap,
  zoom = 1,
  pxPerCm,
  backgroundUrl,
  backgroundOpacity = 0.6,
  backgroundSizeCm,
  onSelect,
  onMove,
  onGesture,
  onPlace,
  tool = 'select',
  liveStateOf,
}: CanvasProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<Drag | null>(null);
  const resizeRef = useRef<Resize | null>(null);
  const frameRef = useRef<number | null>(null);
  const [guides, setGuides] = useState<Guide[]>([]);
  /** The rectangle being dragged out with the room or office tool. */
  const [rubber, setRubber] = useState<{ fromXCm: number; fromYCm: number; xCm: number; yCm: number } | null>(
    null,
  );
  const rubberPointer = useRef<number | null>(null);
  /**
   * Plan cm per screen pixel, so a grip can be a fixed 10 px whatever the
   * zoom: the SVG's width in cm over its width on screen, read after layout.
   */
  const [cmPerPx, setCmPerPx] = useState(1);
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const measure = (): void => {
      const width = svg.getBoundingClientRect().width;
      if (width > 0) setCmPerPx(bounds.widthCm / width);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(svg);
    return () => observer.disconnect();
  }, [bounds.widthCm, zoom]);

  /** Screen → plan coordinates, through the SVG's own matrix so zoom is free. */
  const toPlanCm = (event: ReactPointerEvent): { xCm: number; yCm: number } => {
    const svg = svgRef.current;
    if (!svg) return { xCm: 0, yCm: 0 };
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const matrix = svg.getScreenCTM()?.inverse();
    const mapped = matrix ? point.matrixTransform(matrix) : point;
    return { xCm: mapped.x, yCm: mapped.y };
  };

  const startDrag = (event: ReactPointerEvent, resourceId: string): void => {
    const additive = event.shiftKey;
    const next = additive
      ? selection.includes(resourceId)
        ? selection.filter((id) => id !== resourceId)
        : [...selection, resourceId]
      : selection.includes(resourceId)
        ? selection
        : [resourceId];
    onSelect(next);

    const { xCm, yCm } = toPlanCm(event);
    dragRef.current = {
      pointerId: event.pointerId,
      fromXCm: xCm,
      fromYCm: yCm,
      original: objects.filter((object) => next.includes(object.resourceId)),
    };
    // Capture so a fast drag that leaves the SVG still reaches us.
    event.currentTarget.setPointerCapture(event.pointerId);
    event.stopPropagation();
    onGesture?.('start');
  };

  /** § 6.4 — a grip of a room or an office: the edge follows, the rest stays. */
  const startResize = (event: ReactPointerEvent, object: EditorObject, handle: ResizeHandle): void => {
    const { xCm, yCm } = toPlanCm(event);
    resizeRef.current = { pointerId: event.pointerId, handle, fromXCm: xCm, fromYCm: yCm, original: object };
    event.currentTarget.setPointerCapture(event.pointerId);
    event.stopPropagation();
    onGesture?.('start');
  };

  const onPointerMove = (event: ReactPointerEvent): void => {
    if (rubberPointer.current === event.pointerId && rubber) {
      const { xCm, yCm } = toPlanCm(event);
      setRubber({ ...rubber, xCm, yCm });
      return;
    }
    const resize = resizeRef.current;
    if (resize && resize.pointerId === event.pointerId) {
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        const { xCm, yCm } = toPlanCm(event);
        onMove([
          resizeObject({
            object: resize.original,
            handle: resize.handle,
            deltaXCm: xCm - resize.fromXCm,
            deltaYCm: yCm - resize.fromYCm,
            gridCm,
            snap: snap && !event.altKey,
            bounds,
          }),
        ]);
      });
      return;
    }

    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    // One update per frame: a pointer emits far more events than the screen
    // can show, and re-rendering on each one is what makes a canvas feel slow.
    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const { xCm, yCm } = toPlanCm(event);
      // Alt disables snapping for a fine adjustment (§ 6.3).
      const useSnap = snap && !event.altKey;

      const moved = drag.original.map((object) =>
        moveObject({
          object,
          deltaXCm: xCm - drag.fromXCm,
          deltaYCm: yCm - drag.fromYCm,
          gridCm,
          snap: useSnap,
          bounds,
        }),
      );

      const first = moved[0];
      setGuides(first && !event.altKey ? smartGuides(first, objects, pxPerCm) : []);
      onMove(moved);
    });
  };

  const endDrag = (event: ReactPointerEvent): void => {
    if (rubberPointer.current === event.pointerId && rubber) {
      rubberPointer.current = null;
      setRubber(null);
      const wCm = Math.abs(rubber.xCm - rubber.fromXCm);
      const hCm = Math.abs(rubber.yCm - rubber.fromYCm);
      // A drag draws the rectangle; a mere click places the default size.
      const drawn = wCm >= DRAW_MIN_CM && hCm >= DRAW_MIN_CM;
      onPlace?.(
        drawn ? Math.min(rubber.fromXCm, rubber.xCm) : rubber.fromXCm,
        drawn ? Math.min(rubber.fromYCm, rubber.yCm) : rubber.fromYCm,
        drawn ? { wCm, hCm } : undefined,
      );
      return;
    }
    if (resizeRef.current?.pointerId === event.pointerId) {
      resizeRef.current = null;
      onGesture?.('end');
    }
    if (dragRef.current?.pointerId !== event.pointerId) return;
    dragRef.current = null;
    onGesture?.('end');
    setGuides([]);
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  };

  return (
    <svg
      ref={svgRef}
      data-testid="editor-canvas"
      viewBox={`0 0 ${bounds.widthCm} ${bounds.heightCm}`}
      preserveAspectRatio="xMinYMin meet"
      className="bg-fog block"
      style={{ width: `${zoom * 100}%`, height: 'auto', touchAction: 'none' }}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        onSelect([]);
        if (tool === 'select' || !onPlace) return;
        const { xCm, yCm } = toPlanCm(event);
        if (DRAWN_KINDS.includes(tool)) {
          // § 6.3 — a room or an office is dragged out; the size is its own.
          rubberPointer.current = event.pointerId;
          setRubber({ fromXCm: xCm, fromYCm: yCm, xCm, yCm });
          event.currentTarget.setPointerCapture(event.pointerId);
          return;
        }
        onPlace(xCm, yCm);
      }}
    >
      {backgroundUrl ? (
        <image
          href={backgroundUrl}
          x={0}
          y={0}
          width={backgroundSizeCm?.widthCm ?? bounds.widthCm}
          height={backgroundSizeCm?.heightCm ?? bounds.heightCm}
          opacity={backgroundOpacity}
          preserveAspectRatio="none"
          // A click on the plan is a click on the sheet: it clears the
          // selection or places the tool's object. The picture is not a target.
          pointerEvents="none"
        />
      ) : null}

      <Grid bounds={bounds} gridCm={gridCm} />

      {objects.map((object) => {
        const selected = selection.includes(object.resourceId);
        // The live overlay is display only: it never changes what is saved.
        const style = deskStyle(liveStateOf?.(object.resourceId) ?? stateOf(object));

        return (
          <g
            key={object.resourceId}
            data-testid={`desk-${object.label}`}
            transform={
              object.rotationDeg
                ? `rotate(${object.rotationDeg} ${object.xCm + object.wCm / 2} ${object.yCm + object.hCm / 2})`
                : undefined
            }
          >
            <rect
              x={object.xCm}
              y={object.yCm}
              width={object.wCm}
              height={object.hCm}
              rx={4}
              fill={style.fill}
              stroke={selected ? '#37839d' : style.stroke}
              strokeWidth={selected ? 4 : style.strokeWidth}
              strokeDasharray={style.dash ?? undefined}
              onPointerDown={(event) => startDrag(event, object.resourceId)}
              style={{ cursor: 'move' }}
            />
            <text
              x={object.xCm + object.wCm / 2}
              y={object.yCm + object.hCm / 2}
              textAnchor="middle"
              dominantBaseline="central"
              fill={style.label}
              fontSize={Math.min(object.hCm * 0.4, 28)}
              fontFamily="Figtree, sans-serif"
              fontWeight={600}
              pointerEvents="none"
            >
              {object.label}
            </text>
            {selected && selection.length === 1 && RESIZABLE_KINDS.includes(object.kind)
              ? RESIZE_HANDLES.map((handle) => {
                  const size = GRIP_PX * cmPerPx;
                  const cx =
                    object.xCm +
                    (handle.includes('w') ? 0 : handle.includes('e') ? object.wCm : object.wCm / 2);
                  const cy =
                    object.yCm +
                    (handle.includes('n') ? 0 : handle.includes('s') ? object.hCm : object.hCm / 2);
                  return (
                    <rect
                      key={handle}
                      data-testid={`grip-${object.label}-${handle}`}
                      x={cx - size / 2}
                      y={cy - size / 2}
                      width={size}
                      height={size}
                      fill="#ffffff"
                      stroke="#37839d"
                      strokeWidth={cmPerPx * 1.5}
                      style={{ cursor: GRIP_CURSORS[handle] }}
                      onPointerDown={(event) => startResize(event, object, handle)}
                    />
                  );
                })
              : null}
          </g>
        );
      })}

      {rubber ? (
        <rect
          data-testid="rubber-band"
          x={Math.min(rubber.fromXCm, rubber.xCm)}
          y={Math.min(rubber.fromYCm, rubber.yCm)}
          width={Math.abs(rubber.xCm - rubber.fromXCm)}
          height={Math.abs(rubber.yCm - rubber.fromYCm)}
          fill="#37839d"
          fillOpacity={0.12}
          stroke="#37839d"
          strokeWidth={cmPerPx * 1.5}
          strokeDasharray={`${cmPerPx * 6} ${cmPerPx * 4}`}
          pointerEvents="none"
        />
      ) : null}

      {guides.map((guide, index) => (
        <line
          key={`${guide.axis}-${guide.atCm}-${index}`}
          x1={guide.axis === 'x' ? guide.atCm : 0}
          x2={guide.axis === 'x' ? guide.atCm : bounds.widthCm}
          y1={guide.axis === 'y' ? guide.atCm : 0}
          y2={guide.axis === 'y' ? guide.atCm : bounds.heightCm}
          stroke="#d9e900"
          strokeWidth={2}
          pointerEvents="none"
        />
      ))}
    </svg>
  );
}

/** § 6.3 legend — white is bookable, grey blocked; maintenance is a block row. */
function stateOf(object: EditorObject): DeskState {
  return object.status === 'blocked' ? 'blocked' : 'free';
}

function Grid({ bounds, gridCm }: { bounds: Bounds; gridCm: number }) {
  const id = `grid-${gridCm}`;
  return (
    <>
      <defs>
        <pattern id={id} width={gridCm} height={gridCm} patternUnits="userSpaceOnUse">
          <path d={`M ${gridCm} 0 L 0 0 0 ${gridCm}`} fill="none" stroke="#e9eef0" strokeWidth={1} />
        </pattern>
      </defs>
      <rect width={bounds.widthCm} height={bounds.heightCm} fill={`url(#${id})`} pointerEvents="none" />
    </>
  );
}
